function findByText(text, exact = false) {
	const els = document.querySelectorAll('span, p, label, h6, h5, h4, button, .MuiTypography-root')
	for (const el of els) {
		const content = el.textContent.trim().toLowerCase()
		if (exact ? content === text.toLowerCase() : content.includes(text.toLowerCase())) {
			return el
		}
	}
	return null
}

const ES = navigator.language?.startsWith('es')

export const TOUR_STEPS = [
	{
		type: 'intro',
		icon: 'clipboard-list',
		title: ES ? 'Bienvenido a Lotes' : 'Welcome to Batches',
		description: ES
			? 'La lista de lotes muestra todos los procesos de búsqueda ejecutados. Cada lote agrupa múltiples búsquedas en distintos sitios web y oficinas. Monitoriza el progreso en tiempo real, descarga resultados en Excel, consulta datos históricos o detén lotes en ejecución.'
			: 'The Batch List shows all executed search processes. Each batch groups multiple searches across sites and offices. Monitor progress in real time, download results as Excel, view historical data, or stop running batches.',
	},
	{
		type: 'field',
		findTarget: () => {
			const el = document.querySelector('.rs-picker-daterange') || document.querySelector('[class*="DateRange"]')
			if (el) {
				const container = el.closest('[class*="toolbar"]') || el.parentElement
				return container
			}
			const label = findByText('Date Range')
			if (label) return label.closest('div') || label.parentElement
			return null
		},
		icon: 'calendar-days',
		title: ES ? 'Filtro de rango de fechas' : 'Date Range Filter',
		description: ES
			? 'Filtra los lotes por fecha de creación. Por defecto muestra los lotes de hoy. Selecciona un rango más amplio para ver procesos anteriores. Solo se permiten fechas pasadas y la actual.'
			: 'Filter batches by creation date. By default it shows today\'s batches. Select a wider date range to see older processes. Only past and current dates are allowed.',
	},
	{
		type: 'field',
		findTarget: () => {
			const papers = document.querySelectorAll('.MuiPaper-root, .MuiCard-root')
			for (const paper of papers) {
				if (!paper.textContent.includes('Batch List')) continue
				const rows = paper.querySelectorAll('table tbody tr')
				const hasData = Array.from(rows).some(r =>
					r.querySelectorAll('td').length >= 5 && !r.textContent.includes('no matching records')
				)
				if (hasData) return paper
			}
			return null
		},
		skipIf: () => !findByText('Batch List'),
		icon: 'table-cells',
		title: ES ? 'Tabla de lotes' : 'Batch List Table',
		description: ES
			? 'La tabla muestra todos los lotes con: ID del lote, Sitios (portales web consultados), Oficinas (puntos de recogida), número de búsquedas, fecha de creación y estado actual. Usa las opciones integradas de búsqueda, ordenación y filtrado para encontrar lotes específicos.'
			: 'The table displays all batches with: Batch ID, Sites (web portals searched), Offices (pickup locations), number of searches, creation date, and current status. Use the built-in search, sort, and filter options to find specific batches.',
	},
	{
		type: 'field',
		findTarget: () => {
			const papers = document.querySelectorAll('.MuiPaper-root, .MuiCard-root')
			for (const paper of papers) {
				if (!paper.textContent.includes('Batch List')) continue
				const rows = paper.querySelectorAll('table tbody tr')
				const hasData = Array.from(rows).some(r =>
					r.querySelectorAll('td').length >= 5 && !r.textContent.includes('no matching records')
				)
				if (!hasData) continue
				// Find the "Stopped" row for visual impact
				const stoppedRow = Array.from(rows).find(r => r.textContent.includes('Stopped'))
				const targetRow = stoppedRow || Array.from(rows).find(r => r.querySelectorAll('td').length >= 5)
				if (targetRow) {
					const cells = targetRow.querySelectorAll('td')
					return cells[cells.length - 1]
				}
			}
			return null
		},
		skipIf: () => !findByText('Batch List'),
		icon: 'signal',
		title: ES ? 'Columna de estado' : 'Status Column',
		description: ES
			? 'La columna más detallada. Para lotes en ejecución: una barra de progreso clicable que muestra tareas completadas/totales (haz clic para ver el detalle por sitio). Para lotes detenidos: un icono de mano con la etiqueta "Detenido". Para todos los lotes: contador de errores (icono de advertencia) y contador N/A (icono de calendario) que muestran problemas en las búsquedas.'
			: 'The most detailed column. For running batches: a clickable progress bar showing completed/total tasks (click to view per-site details). For stopped batches: a hand icon with "Stopped" label. For all batches: error count (warning icon) and N/A count (calendar icon) showing search issues.',
	},
	{
		type: 'field',
		findTarget: () => {
			const papers = document.querySelectorAll('.MuiPaper-root, .MuiCard-root')
			for (const paper of papers) {
				if (!paper.textContent.includes('Batch List')) continue
				const rows = paper.querySelectorAll('table tbody tr')
				const hasData = Array.from(rows).some(r =>
					r.querySelectorAll('td').length >= 5 && !r.textContent.includes('no matching records')
				)
				if (!hasData) continue
				const firstRow = Array.from(rows).find(r => r.querySelectorAll('td').length >= 5)
				if (firstRow) {
					const cells = firstRow.querySelectorAll('td')
					return cells[cells.length - 1]
				}
			}
			return null
		},
		skipIf: () => !findByText('Batch List'),
		icon: 'download',
		title: ES ? 'Botones de acción' : 'Action Buttons',
		description: ES
			? 'Cada fila de lote tiene botones de acción. Descargar exportación (nube verde, si está disponible): descarga el archivo exportado. Ver histórico (reloj azul): abre las búsquedas del lote en la página de Historial. Descargar Excel (hoja de cálculo verde): descarga todos los resultados del lote en un archivo Excel.'
			: 'Each batch row has action buttons. Download Export (green cloud, if available): download the exported file. View Historical (blue clock): opens the batch searches in the History page. Download Excel (green spreadsheet): downloads all batch results as an Excel file.',
		example: ES
			? '☁️ Descargar exportación | 🕐 Ver histórico | 📊 Descargar Excel'
			: '☁️ Download Export | 🕐 View Historical | 📊 Download Excel',
	},
	{
		type: 'field',
		findTarget: () => {
			const checkboxes = document.querySelectorAll('table thead input[type="checkbox"], table thead .MuiCheckbox-root')
			if (checkboxes.length > 0) return checkboxes[0].closest('th') || checkboxes[0]
			return null
		},
		skipIf: () => !document.querySelector('table thead input[type="checkbox"]') && !document.querySelector('table thead .MuiCheckbox-root'),
		icon: 'circle-stop',
		title: ES ? 'Selección múltiple y detención' : 'Multi-Select & Stop',
		description: ES
			? 'Selecciona varios lotes usando las casillas de verificación de cada fila. Al seleccionar filas, aparece una barra de herramientas con un botón "Detener múltiples ejecuciones" para forzar la detención de todos los lotes en ejecución seleccionados a la vez.'
			: 'Select multiple batches using the checkboxes on each row. When rows are selected, a toolbar appears with a "Stop multiple executions" button to force-stop all selected running batches at once.',
	},
	{
		type: 'outro',
		icon: 'star',
		title: ES ? '¡Todo listo!' : "You're All Set!",
		description: ES
			? 'Usa el rango de fechas para filtrar lotes, monitoriza los procesos en ejecución mediante la barra de progreso y descarga los resultados cuando estén listos. Haz clic en la barra de progreso de un lote en ejecución para ver el detalle por sitio. Usa la selección múltiple para detener lotes en bloque.'
			: 'Use the date range to filter batches, monitor running processes via the progress bar, and download results when ready. Click the progress bar of a running batch to see per-site details. Use multi-select to stop batches in bulk.',
	},
]
